package mapsearch

import apiclient.geoobjects.GeoObjectDetails
import apiclient.mapsearch.MapFeature
import apiclient.mapsearch.MapMarker
import apiclient.mapsearch.MarkerCluster
import apiclient.util.isNotNullOrEmpty
import dev.fritz2.core.Id
import dev.fritz2.core.RootStore
import koin.withKoin
import kotlinx.coroutines.Job
import maplibreGL.FormationMap
import maplibreGL.Marker
import maplibreGL.toLngLat
import mapsearch.generalMarkers.GeneralMarkersSearchSpecStore
import mapsearch.globalsearch.toMapMarker
import mapsearch.zones.ZoneSearchSpecStore

private const val CLUSTER_RADIUS_PIXEL = 40.0

data class ClusterData(
    val mapSearchFeatures: List<MapFeature> = emptyList(),
    val clusterOfNotFetched: List<MapFeature> = emptyList(),
    val zoneFeatures: Map<String, GeoObjectDetails> = emptyMap(),
    val generalMarkerFeatures: Map<String, GeoObjectDetails> = emptyMap(),
) {
    val sum
        get() = mapSearchFeatures +
//            zoneFeatures.values.map { it.toMapMarker() } +
            generalMarkerFeatures.values.map { it.toMapMarker() }

    val ids get() = sum.filterIsInstance<MapMarker>().map { it.id }
}

class ClusterService : RootStore<ClusterData>(ClusterData(), Job()) {

    private var renderedFeatures: MutableMap<String, Pair<MapFeature, Marker>> = mutableMapOf()
    private var renderedClusters: MutableMap<String, Pair<MapFeature, Marker>> = mutableMapOf()

    var excludeFromClusterFilter: ((MapFeature) -> Boolean)? = null

    fun setFeaturesFromMapSearch(
        features: Map<String, List<MapFeature>>,
        clusters: Map<String, List<MapFeature>>
    ) {
//        console.log(
//            "^ Set Features from MapSearch:",
//            features.values.map { it.map { m -> if (m is MapMarker) "${m.title}, ${m.position}" else null } }.toString(),
//        )
        update(
            current.copy(
                mapSearchFeatures = features.values.flatten(),
                clusterOfNotFetched = clusters.values.flatten(),
            ),
        )
    }

    fun updateFeatureMarkers(newMarkers: List<MapMarker>) {
//        console.log("^ Update FeatureMarkers on map:", newMarkers.map { "${it.title}, ${it.position}" }.toString())
        newMarkers.forEach { feature ->
            renderedFeatures.remove(feature.id)?.let { (feature, marker) ->
                marker.remove()
            }
        }
        renderMapSearchFeatures(newMarkers)
    }

    fun removeFeaturesFromMap(featureMarkerIds: List<String>) {
        // remove markers from map
        featureMarkerIds.forEach { featureId ->
            renderedFeatures[featureId]?.let { (_, marker) ->
                marker.remove()
            }
        }
    }

    fun removeFeatures(deleteIds: List<String>) {
        deleteIds.forEach { featureId ->
            renderedFeatures.remove(featureId)?.let { (feature, marker) ->
                marker.remove()
            }
        }
        update(
            current.copy(
                mapSearchFeatures = current.mapSearchFeatures.filterIsInstance<MapMarker>()
                    .filter { it.id !in deleteIds },
                zoneFeatures = current.zoneFeatures.filter { it.key !in deleteIds },
                generalMarkerFeatures = current.generalMarkerFeatures.filter { it.key !in deleteIds },
            ),
        )
    }

    fun updateFeatures(updatedMarkers: List<MapMarker>? = null, newMarkers: List<MapMarker>? = null) {

        var updatedFeatures: List<MapFeature> = emptyList()
        val changedFeatures: MutableList<MapMarker> = mutableListOf()
        val updatedZoneFeatures: MutableMap<String, GeoObjectDetails> = mutableMapOf()
        val updatedGeneralMarkerFeatures: MutableMap<String, GeoObjectDetails> = mutableMapOf()

        // map over newMarkers + updated, in case newMarkers are already in current
        updatedMarkers?.let { markers ->
            // Create a lookup map for quick access to new markers by ID
            val updatedMarkersMap = markers.associateBy { it.id }.toMutableMap()

            // Replace old MapMarkers with new ones
            updatedFeatures = current.mapSearchFeatures.map { feature ->
                if (feature is MapMarker && updatedMarkersMap.containsKey(feature.id)) {
                    updatedMarkersMap.remove(feature.id)!!.let {
                        changedFeatures.add(it)
                        it
                    }
                } else {
                    feature
                }
            }

            current.zoneFeatures.map { (key, feature) ->
                updatedZoneFeatures[key] = if (updatedMarkersMap.containsKey(key)) {
                    feature.updateFromMapMarker(updatedMarkersMap.remove(key)!!)
                } else {
                    feature
                }
            }

            current.generalMarkerFeatures.map { (key, feature) ->
                updatedGeneralMarkerFeatures[key] = if (updatedMarkersMap.containsKey(key)) {
                    feature.updateFromMapMarker(updatedMarkersMap.remove(key)!!)
                } else {
                    feature
                }
            }
        }

        // This call here to update Markers on the map
        updateFeatureMarkers(newMarkers.orEmpty() + changedFeatures)

        if (updatedMarkers.isNotNullOrEmpty() || newMarkers.isNotNullOrEmpty()) {
            update(
                current.copy(
                    mapSearchFeatures = newMarkers.orEmpty() + updatedFeatures,
                    zoneFeatures = updatedZoneFeatures,
                    generalMarkerFeatures = updatedGeneralMarkerFeatures,
                ),
            )
        }
    }

    fun setFeaturesFromZoneSearch(features: Map<String, GeoObjectDetails>) {
        update(current.copy(zoneFeatures = features))
    }

    fun setFeaturesFromGeneralMarkerSearch(features: Map<String, GeoObjectDetails>) {
        update(current.copy(generalMarkerFeatures = features))
    }

    private fun clusterFeaturesByPixelThreshold(
        features: List<MapFeature>,
        forcedNoiseFilter: ((MapFeature) -> Boolean) = excludeFromClusterFilter ?: { false }
    ): Pair<List<MapFeature>, List<List<MapFeature>>> {
        withKoin {
            val formationMap: FormationMap = get()
            val map = formationMap.map

            val forcedNoise: MutableList<MapFeature> = mutableListOf()

            val filtered = features.mapNotNull { feature ->
                if (forcedNoiseFilter(feature)) {
                    forcedNoise.add(feature)
                    null
                } else feature
            }

            val (noise, clusters) = dbScanCluster(
                items = filtered,
                distanceThreshold = CLUSTER_RADIUS_PIXEL * CLUSTER_RADIUS_PIXEL, // multiply here to omit sqrt
                minClusterSize = 2,
                distanceFunction = { left, right ->
                    val a = left.position?.toLngLat()?.let {
                        map?.project(lngLat = it)
                    }?.let {
                        arrayOf(it.x, it.y)
                    }

                    val b = right.position?.toLngLat()?.let {
                        map?.project(lngLat = it)
                    }?.let {
                        arrayOf(it.x, it.y)
                    }

                    if (a != null && b != null) {
                        val (x1, y1) = a
                        val (x2, y2) = b
                        val dx = x1 - x2
                        val dy = y1 - y2

                        // omit sqrt operation (distanceThreshold is multiplied)
                        (dx * dx) + (dy * dy)
                    } else {
                        0.0
                    }
                },
            )
            return Pair(forcedNoise + noise, clusters)
        }
    }

    suspend fun cluster(features: List<MapFeature>, additionalClusters: List<MapFeature>) {
//        console.log("^ Cluster these:", features.map { if (it is MapMarker) it.title else null }.toString())
        val (noise, clusters) = clusterFeaturesByPixelThreshold(features + additionalClusters)
        val newClusteredFeatures = clusters.map { cluster ->
            val iterator = cluster.iterator()
            var left = iterator.next()
            while (iterator.hasNext()) {
                val right = iterator.next()
                left = combineMapFeaturesToCluster(left, right)
            }
            left
        } + noise

        // clear all clusters and features from map
        renderedClusters.forEach { (_, marker) ->
            marker.second.remove()
        }.also {
            renderedClusters.clear()
        }
        renderedFeatures.forEach { (_, marker) ->
            marker.second.remove()
        }.also {
            renderedFeatures.clear()
        }

        val noiseIds = noise.filterIsInstance<MapMarker>().map { it.id }
        renderZones(withinIds = noiseIds)
        renderGeneralMarkers(withinIds = noiseIds)
        renderMapSearchFeatures(newClusteredFeatures)
    }

    private fun renderZones(withinIds: List<String>) {
        withKoin {
            val zoneSearchSpecStore: ZoneSearchSpecStore = get()
            zoneSearchSpecStore.renderZones(
                // NOTE: do not cluster zones -> ignore ids and render all
                current.zoneFeatures,//.filterKeys { it in withinIds },
            )
        }

    }

    private suspend fun renderGeneralMarkers(withinIds: List<String>) {
        withKoin {
            val generalMarkersSearchSpecStore: GeneralMarkersSearchSpecStore = get()
            generalMarkersSearchSpecStore.renderGeneralMarkers(
                current.generalMarkerFeatures.filterKeys { it in withinIds },
            )
        }

    }

    private fun renderMapSearchFeatures(features: List<MapFeature>) {
//        console.log("^ Render These:", features.map { if (it is MapMarker) "${it.title}, ${it.position}" else null }.toString())
        withKoin {
            val formationMap: FormationMap = get()
            val mapRenderOverrides: MapRenderOverrides = get()
            val map = formationMap.map

            if (map != null) {
                features.forEach { feature ->
                    when (feature) {
                        is MapMarker -> {
                            if (
                            // Filter out zones and generalMarkers again
                                feature.id !in current.zoneFeatures.keys
                                && feature.id !in current.generalMarkerFeatures.keys
                                && !isUser(feature.id)
                            ) {
                                val mapLibreMarker = MarkerGenerators.createMapLibreMarker(feature)
                                if (feature.id !in mapRenderOverrides.hiddenMarkers) {
//                                    console.log("^ Added Marker to Map for:", feature.title)
                                    mapLibreMarker.addTo(map)
                                }
                                renderedFeatures.remove(feature.id)?.let { (_, marker) ->
                                    marker.remove()
                                }
                                renderedFeatures[feature.id] = Pair(feature, mapLibreMarker)
                            }
                        }

                        is MarkerCluster -> {
                            val newClusterId = "cl-${feature.tilePath}-${Id.next(5)}"
                            val mapLibreMarker = MarkerGenerators.createClusterMarker(feature, newClusterId)
                            mapLibreMarker.addTo(map)
                            renderedClusters[newClusterId] = Pair(feature, mapLibreMarker)
                        }

                        else -> {}
                    }
                }
            }
        }
    }

    init {
        data handledBy { clusterFeatures ->
            cluster(
                features = clusterFeatures.sum,
                additionalClusters = clusterFeatures.clusterOfNotFetched,
            )
        }
    }
}
